import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useContext, useEffect, useState } from "react";
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ComplexFilterContext } from "./ComplexFilterContext";
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

/**
 * Filter value control that resolves its options from a query def at runtime.
 *
 * Header config example:
 *   {
 *     machineName: 'user__username',
 *     humanName: 'User',
 *     inputType: 'autocomplete',
 *     operators: stringOperators,
 *     autocomplete: {
 *       queryDef: getAuditLogDistinctValues,
 *       queryArgs: { field: 'user__username' },   // forwarded to queryDef.func
 *       resultsPath: 'results',                   // optional, default 'results'
 *     }
 *   }
 *
 * The query is searched in-process by typing — server-side `search` is also
 * forwarded so very long lists don't have to download in full.
 */
const FilterAutocomplete = ({ header, value, onChange }) => {
  const [search, setSearch] = useState('');
  const config = header.autocomplete || {};
  const queryDef = config.queryDef;
  const baseArgs = config.queryArgs || {};
  const limit = config.limit ?? 50;
  const optionLabel = config.optionLabel || ((opt) => (opt == null ? '' : String(opt)));
  const optionValue = config.optionValue || ((opt) => opt);
  const optionSecondary = config.optionSecondary || (() => '');

  const query = useQuery({
    queryKey: [queryDef?.key, baseArgs, search, limit],
    queryFn: () => queryDef.func({ ...baseArgs, search, limit }),
    enabled: !!queryDef,
    placeholderData: keepPreviousData,
    staleTime: 30_000
  });

  const resultsPath = config.resultsPath || 'results';
  const options = (query.data?.[resultsPath]) || [];
  const total = query.data?.count;
  const hasMore = typeof total === 'number' && total > options.length;
  const helper = query.isFetching
    ? null
    : hasMore
      ? `Showing first ${options.length} of ${total} matches — keep typing to narrow.`
      : (typeof total === 'number'
          ? `${total} match${total === 1 ? '' : 'es'}.`
          : null);

  return (
    <Autocomplete
      freeSolo
      options={options}
      value={value ?? null}
      inputValue={search}
      onInputChange={(_e, newInput) => setSearch(newInput)}
      onChange={(_e, newValue) => {
        if (newValue == null || typeof newValue === 'string') {
          onChange(newValue ?? '');
        } else {
          onChange(optionValue(newValue) ?? '');
        }
      }}
      filterOptions={(opts) => opts}
      getOptionLabel={(opt) => (typeof opt === 'string' ? opt : optionLabel(opt))}
      isOptionEqualToValue={(opt, val) => optionValue(opt) === val}
      renderOption={(props, opt) => {
        // MUI 5.0.0-alpha.32's Autocomplete does not auto-inject a unique
        // `key` into `props`, so set one explicitly. Combine value + secondary
        // so company-vs-merchant rows with the same display name stay unique.
        const label = optionLabel(opt);
        const secondary = optionSecondary(opt);
        const key = `${optionValue(opt) ?? label}__${secondary ?? ''}`;
        return (
          <li {...props} key={key}>
            <span>{label}</span>
            {secondary && (
              <span style={{ marginLeft: 8, opacity: 0.6, fontSize: '0.85em' }}>
                · {secondary}
              </span>
            )}
          </li>
        );
      }}
      loading={query.isFetching}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Value"
          fullWidth
          helperText={helper}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {query.isFetching ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

const ComplexFilterRow = ({groupId, filter: thisFilter}) => {
  const {filterHeaders, setFilter, removeFilter} = useContext(ComplexFilterContext);
  const {filterId} = thisFilter;
  const selectedHeader = filterHeaders.filter((header) => header.machineName === thisFilter.column)[0];

  useEffect(() => {
    // This effect will run when the datetime is selected
    if (selectedHeader.inputType === 'datetime' && thisFilter.value === undefined) {
      // Set the initial value to the current date if it's undefined
      setFilter(groupId, filterId, {
        ...thisFilter,
        value: new Date(),
      });
    }
  }, [selectedHeader]);

  // When `isnull` is chosen, the value control no longer matches the
  // column's natural type (autocomplete / datetime / number → boolean
  // True/False). Seed a default `true` so the row immediately encodes
  // a working filter, and clear the value when the operator moves
  // back to a type-specific one so a stale boolean doesn't get
  // serialised as a string into an autocomplete query.
  useEffect(() => {
    if (thisFilter.operator === 'isnull') {
      if (typeof thisFilter.value !== 'boolean') {
        setFilter(groupId, filterId, { ...thisFilter, value: true });
      }
    } else if (typeof thisFilter.value === 'boolean' && selectedHeader?.inputType !== 'boolean') {
      // Carry over from isnull → something else; reset to undefined so
      // the natural input opens empty. Boolean-typed columns are exempt:
      // their value is legitimately a boolean (e.g. a restored `Amex is
      // True` row mounts with operator 'exact' and value true — wiping it
      // here would silently widen the filter on the next Apply).
      setFilter(groupId, filterId, { ...thisFilter, value: undefined });
    }
  }, [thisFilter.operator]);

  // Renders the value control for the selected column type. Kept as a local
  // because the input-type dispatch is verbose enough that splitting it out
  // makes the row layout below easier to read.
  const renderValueControl = () => {
    const setValue = (newValue) => setFilter(groupId, filterId, { ...thisFilter, value: newValue });
    // `isnull` short-circuits whatever inputType the column normally
    // uses — the only meaningful value is True (empty) or False (not
    // empty). This makes the autocomplete/datetime/etc. picker drop
    // away when the operator is chosen, so the operator on its own
    // produces a working filter without forcing the user to pick a
    // company / date / number that the backend would ignore anyway.
    if (thisFilter.operator === 'isnull') {
      return (
        <FormControl fullWidth size="small">
          <InputLabel>Value</InputLabel>
          <Select
            value={typeof thisFilter.value === 'boolean' ? thisFilter.value : true}
            label="Value"
            onChange={(e) => setValue(e.target.value)}
          >
            <MenuItem value={true}>True</MenuItem>
            <MenuItem value={false}>False</MenuItem>
          </Select>
        </FormControl>
      );
    }
    switch (selectedHeader.inputType) {
      case 'text':
        return (
          <TextField
            label="Value"
            size="small"
            fullWidth
            value={thisFilter.value ?? ''}
            type="text"
            onChange={(e) => setValue(e.target.value)}
          />
        );
      case 'number':
        return (
          <TextField
            label="Value"
            size="small"
            fullWidth
            value={thisFilter.value ?? ''}
            type="number"
            onChange={(e) => setValue(e.target.value)}
          />
        );
      case 'boolean':
        return (
          <FormControl fullWidth size="small">
            <InputLabel>Value</InputLabel>
            <Select
              value={thisFilter.value ?? ''}
              label="Value"
              onChange={(e) => setValue(e.target.value)}
            >
              <MenuItem value={true}>True</MenuItem>
              <MenuItem value={false}>False</MenuItem>
            </Select>
          </FormControl>
        );
      case 'choices': {
        // `in` rows hold an array of choice values (encoded as `<column>__in`);
        // render them as a multi-select listing the choice names.
        const multiple = thisFilter.operator === 'in';
        const value = multiple
          ? (Array.isArray(thisFilter.value) ? thisFilter.value : [])
          : (thisFilter.value ?? '');
        return (
          <FormControl fullWidth size="small">
            <InputLabel>Value</InputLabel>
            <Select
              multiple={multiple}
              value={value}
              label="Value"
              renderValue={multiple
                ? (selected) => selectedHeader.choices
                  ?.filter((choice) => selected.includes(choice.value))
                  .map((choice) => choice.key)
                  .join(', ')
                : undefined}
              onChange={(e) => setValue(e.target.value)}
            >
              {selectedHeader.choices?.map((choice) => (
                <MenuItem key={String(choice.value)} value={choice.value}>
                  {choice.key}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }
      case 'datetime':
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
              label="Value"
              ampm={false}
              value={thisFilter.value || new Date()}
              onChange={(newValue) => setValue(newValue)}
            />
          </LocalizationProvider>
        );
      case 'autocomplete':
        return (
          <FilterAutocomplete
            header={selectedHeader}
            value={thisFilter.value}
            onChange={setValue}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        opacity: thisFilter.active ? 1 : 0.55,
        p: 1.25,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        transition: 'opacity 120ms',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', md: 'center' }}
      >
        <Tooltip title={thisFilter.active ? 'Filter is active' : 'Filter is paused'}>
          <Switch
            size="small"
            color="primary"
            checked={thisFilter.active}
            onChange={() =>
              setFilter(groupId, filterId, { ...thisFilter, active: !thisFilter.active })
            }
          />
        </Tooltip>

        <FormControl size="small" sx={{ minWidth: 160, flexGrow: 1 }}>
          <InputLabel>Column</InputLabel>
          <Select
            value={thisFilter.column}
            label="Column"
            onChange={(event) => {
              setFilter(groupId, filterId, { ...thisFilter, column: event.target.value });
            }}
          >
            {filterHeaders.map((header) => (
              <MenuItem key={header.machineName} value={header.machineName}>
                {header.humanName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tooltip title={thisFilter.type ? 'Include matching rows' : 'Exclude matching rows'}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={thisFilter.type ? 'include' : 'exclude'}
            onChange={(_e, v) => {
              if (v == null) return;
              setFilter(groupId, filterId, { ...thisFilter, type: v === 'include' });
            }}
          >
            <ToggleButton value="include" color="primary">Include</ToggleButton>
            <ToggleButton value="exclude" color="error">Exclude</ToggleButton>
          </ToggleButtonGroup>
        </Tooltip>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Operator</InputLabel>
          <Select
            value={thisFilter.operator}
            label="Operator"
            onChange={(event) => {
              setFilter(groupId, filterId, { ...thisFilter, operator: event.target.value });
            }}
          >
            {selectedHeader.operators.map((operator) => (
              <MenuItem key={operator.machineName} value={operator.machineName}>
                {operator.humanName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ flexGrow: 2, minWidth: 200 }}>
          {renderValueControl()}
        </Box>

        <Tooltip title="Remove this filter">
          <IconButton size="small" onClick={() => removeFilter(groupId, filterId)}>
            <ClearIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
};

export {
  ComplexFilterRow as default
};
